/**
 * Owl Ops Analytics Client (Page Views Only)
 *
 * Minimal client for tracking page views via Cloudflare Worker proxy.
 * All other events are tracked server-side via the Django API.
 */

// Cookie name - matches backend middleware
const VISITOR_ID_COOKIE = '_io_vid';

// Tracking endpoint (matches worker)
const ENDPOINT = '/_/sync-cache';

// --- Obfuscation (matches worker decode) ---

function encode(str: string): string {
  try {
    const ts = Date.now().toString(36).slice(-8).padStart(8, '0');
    const bytes = new TextEncoder().encode(str);
    const encoded = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      encoded[i] = bytes[i] ^ ts.charCodeAt(i % ts.length);
    }
    let binary = '';
    for (let i = 0; i < encoded.length; i++) {
      binary += String.fromCharCode(encoded[i]);
    }
    return ts + btoa(binary);
  } catch {
    return '';
  }
}

function sendObfuscated(type: 'e', data: unknown): void {
  try {
    const payload = JSON.stringify({ t: type, d: data });
    const body = encode(payload);
    if (!body) return;

    // Fire-and-forget with sendBeacon for reliability on navigation
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, body);
    } else {
      fetch(ENDPOINT, { method: 'POST', body, keepalive: true });
    }
  } catch {
    // Silently fail - analytics should never crash the app
  }
}

// --- Cookie Helper ---

function getCookie(name: string): string | null {
  try {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

// --- Visitor ID ---

export function getVisitorId(): string {
  try {
    if (typeof window === 'undefined') return '';
    return getCookie(VISITOR_ID_COOKIE) || '';
  } catch {
    return '';
  }
}

// --- Page View Tracking ---

interface PageViewProperties {
  page_path?: string;
  page_url?: string;
  referrer?: string;
  title?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

export function trackPageView(properties?: PageViewProperties): void {
  try {
    if (typeof window === 'undefined') return;

    const visitorId = getVisitorId();
    if (!visitorId) return;

    // Build obfuscated properties
    // a=page_path, b=page_url, c=referrer, d=title, e-g=utm params
    const props: Record<string, unknown> = {};
    if (properties?.page_path) props.a = properties.page_path;
    if (properties?.page_url) props.b = properties.page_url;
    if (properties?.referrer) props.c = properties.referrer;
    if (properties?.title) props.d = properties.title;
    if (properties?.utm_source) props.e = properties.utm_source;
    if (properties?.utm_medium) props.f = properties.utm_medium;
    if (properties?.utm_campaign) props.g = properties.utm_campaign;

    // Fallback to window values if not provided
    if (!props.a) props.a = window.location.pathname;
    if (!props.b) props.b = window.location.href;
    if (!props.c && document.referrer) props.c = document.referrer;
    if (!props.d && document.title) props.d = document.title;

    // Build obfuscated payload
    // a=id, b=event_code, c=session_id, d=properties, e='a' for anonymous
    const payload: Record<string, unknown> = {
      a: visitorId,
      b: 'x1', // page_view event code
      c: crypto.randomUUID().slice(0, 16), // session id
      d: props,
      e: 'a', // anonymous flag
    };

    sendObfuscated('e', payload);
  } catch {
    // Silently fail
  }
}
